import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Callout, ConfirmDialog, EmptyState, FormRow, FormSection, ImageDropzone, NumberField, PhoneFrame,
  AuditTrail, SaveBar, SummaryStrip, Toolbar, ghostBtnCls, inputCls, secondaryBtnCls, textareaCls, undoReason, useToast,
  type ImageValue, type SummaryStat,
  resolveLink, type AuditEntry, type NavLinkComponent,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  BENEFIT_CATALOG, TIERS, membersInRange,
  type BenefitCatalogItem, type Tier, type TierBenefit, type TierId,
} from './loyaltyData';

/**
 * TierScreen — ระดับสมาชิกและสิทธิประโยชน์ (FR-ADM-23)
 *
 * ⚠️ **SRS กับ backend ขัดกันในเรื่องนี้ และหน้าจอทำตาม backend**
 *
 *    SRS ข้อ 3.4 เขียนว่า "ข้อความสิทธิประโยชน์แต่ละรายการต้องเป็นช่องข้อความที่ Admin แก้ไขได้เอง"
 *    แต่ backend เก็บรหัสสิทธิ์เป็น enum ตายตัว และมีฟังก์ชันซิงก์ตารางแม่กับ enum นั้น
 *    การพิมพ์ข้อความอิสระจึงไม่มีที่เก็บ — เคยทำเป็นช่องข้อความแล้วบันทึกไม่ได้จริง
 *
 *    หน้าจอจึงเปลี่ยนเป็น "เลือกจากรหัสที่ระบบมี" แล้วตั้งเพดานต่อคนได้
 *    ถ้าธุรกิจยังต้องการข้อความอิสระตาม SRS ต้องให้ backend เพิ่มฟิลด์ข้อความในตารางเชื่อมก่อน
 *
 * สามเรื่องที่ตัดสินใจไว้:
 *
 *   1. **เกณฑ์ยอดซื้อทั้งสามระดับอยู่ในตารางเดียวกัน** ไม่แยกไปหน้าละระดับ
 *      เพราะความผิดพลาดที่เกิดจริงคือช่องว่างและช่วงทับกัน (Silver จบ 5,000 แต่ Gold เริ่ม 5,500)
 *      ซึ่งมองเห็นได้เฉพาะเมื่อเห็นทั้งสามค่าพร้อมกัน
 *      ขั้นสูงของแต่ละระดับจึงไม่ให้กรอก — คำนวณจากขั้นต่ำของระดับถัดไป ทำให้ช่องว่างเกิดขึ้นไม่ได้
 *
 *   2. **ข้อความสิทธิประโยชน์นับตัวอักษรเทียบกับความกว้างจริงของแอป** ที่ 390px
 *      ข้อความเกิน 62 ตัวอักษรจะตัดเป็นสองบรรทัด ซึ่งไม่ผิดแต่ทำให้การ์ดสิทธิ์สูงไม่เท่ากัน
 *      หน้าจอจึงบอกตอนพิมพ์ ไม่ใช่ให้ไปเห็นในแอป
 *
 *   3. **พรีวิวเป็นกรอบมือถือจริงข้าง ๆ ตัวแก้** เพราะสิ่งที่แก้คือข้อความที่ลูกค้าอ่าน
 *      ไม่ใช่ค่าตั้งค่าของระบบ ถ้าไม่เห็นบริบทจะเขียนข้อความที่ยาวเกินหรือสั้นเกินความหมาย
 *
 * ⚠ เพดานการใช้สิทธิ์ต่อคนต่อปี (BR-05 — เช่น 1 สิทธิ์/ปี) ผูกกับสิทธิ์แต่ละรายการ
 *   ไม่ใช่กับระดับสมาชิก เพราะ SRS 3.4 ระบุเพดานไว้ต่างกันในแต่ละสิทธิ์
 */

type ScreenState = 'ready' | 'loading' | 'error';

const uid = () => `b-${Math.random().toString(36).slice(2, 8)}`;

/** สีพื้นของการ์ดระดับในพรีวิว — ใช้ค่าที่มีอยู่แล้วในระบบ ไม่เพิ่มสีใหม่ */
/**
 * สีระดับสมาชิก — **ต้องตรงกับแอปลูกค้าเป๊ะ**
 *
 * ⚠️ เดิมหน้านี้เดาสีเอง (เงิน = surface-100 · ทอง = lime-400 · แพลทินัม = blue-700)
 *    ซึ่งไม่ตรงกับแอปที่ลูกค้าเห็นจริงเลยสักระดับ ทำให้คนตั้งค่าที่หลังบ้าน
 *    เห็นคนละสีกับที่ลูกค้าจะเห็น — ตัดสินใจเรื่องภาพประกอบผิดได้
 *
 * ตอนนี้อ้าง token ชุดเดียวกับ TierBadge ของแอปมือถือ (--tier-*)
 * ถ้าจะเปลี่ยนสี ต้องเปลี่ยนที่ token แล้วมีผลทั้งสองระบบพร้อมกัน
 */
type TierStyle = { bg: string; text: string; border: string; onLight: string; icon: string };

/**
 * ⚠️ สีจัดตาม **ลำดับของระดับ** (ต่ำ → สูง) ไม่ได้ผูกกับชื่อแล้ว
 *    เพราะตอนนี้เพิ่ม ลบ และตั้งชื่อระดับได้อิสระ ชื่อจึงบอกไม่ได้ว่าควรเป็นสีอะไร
 *    มี token สีอยู่ 3 ชุด — ระดับที่ 3 ขึ้นไปใช้ชุดสูงสุด (platinum) ร่วมกัน
 */
const TIER_STYLES: TierStyle[] = [
  {
    bg: 'var(--tier-silver-solid)', text: 'var(--tier-silver-on)',
    border: 'var(--tier-silver-border)', onLight: 'var(--tier-silver-border)',
    icon: 'workspace_premium',
  },
  {
    bg: 'var(--tier-gold-solid)', text: 'var(--tier-gold-on)',
    border: 'var(--tier-gold-border)', onLight: 'var(--tier-gold-border)',
    icon: 'workspace_premium',
  },
  {
    bg: 'var(--tier-platinum-solid)', text: 'var(--tier-platinum-on)',
    border: 'var(--tier-platinum-border)',
    /**
     * ⚠️ platinum ใช้ border บนพื้นอ่อนไม่ได้
     *    --tier-platinum-border (#C8CDD6) ออกแบบมาให้ตัดกับ **พื้นน้ำเงิน** (6.89:1)
     *    เอามาวางบนพื้นขาวได้แค่ 1.60:1 ซึ่งตกเกณฑ์หนัก
     *    บนพื้นอ่อนจึงใช้ solid (#14171C) ที่เกือบดำแทน
     */
    onLight: 'var(--tier-platinum-solid)',
    icon: 'diamond',
  },
];

const styleAt = (index: number): TierStyle => TIER_STYLES[Math.max(0, Math.min(index, TIER_STYLES.length - 1))]!;

/** id ชั่วคราวของระดับที่เพิ่งเพิ่ม — ชั้นข้อมูลรู้ว่าต้องสร้างใหม่ */
const NEW_TIER_PREFIX = 'new-tier-';


export interface TierScreenProps {
  /** host เป็นคนตัดสินว่ากดลิงก์แล้วไปไหน — ดู components/NavLink */
  linkComponent?: NavLinkComponent;
  initialState?: ScreenState;
  /**
   * ระดับสมาชิกจากชั้น API — ถ้าไม่ส่งมาจะใช้ข้อมูลจำลองในไฟล์
   * (ใช้ตอนดูหน้านี้ใน /foundations โดยไม่ต้องมี backend)
   */
  tiers?: Tier[];
  /** บันทึกกลับไปที่ backend — คืน error เพื่อให้หน้าจอแสดงและคืนค่าเดิม */
  /** `reason` = เหตุผลจากกล่องยืนยัน — backend เก็บใน Audit Log ของทุกคำขอในรอบบันทึกนั้น */
  onSave?: (next: Tier[], reason?: string) => Promise<void>;
  /**
   * แสดงช่องอัปโหลดภาพประกอบของระดับหรือไม่
   *
   * ⚠️ ระบบหลังบ้าน **ไม่มีที่เก็บภาพของระดับสมาชิก** — ต่างจากของรางวัลและสิทธิพิเศษ
   *    ถ้าเปิดไว้ ผู้ใช้จะอัปโหลดแล้วเห็นว่าบันทึกสำเร็จ แต่ภาพหายเมื่อโหลดหน้าใหม่
   */
  showImage?: boolean;
  /**
   * ป้ายของเพดานการกดรับต่อคน — ระบบหลังบ้านนับ **ต่อรอบ** (สัปดาห์/เดือน/ปี)
   * ไม่ได้นับต่อปีเสมอไป จึงให้ชั้นข้อมูลเป็นคนบอกคำที่ตรงกับของจริง
   */
  capLabel?: string;
  /**
   * แสดง "ปีนี้กดรับไปแล้ว" หรือไม่ — ไม่มี endpoint ให้นับ จึงปิดเมื่อต่อ backend จริง
   * เลขศูนย์ตลอดอ่านว่า "ไม่มีใครกดรับ" ซึ่งไม่จริง
   */
  showClaimedCount?: boolean;
  /**
   * จำนวนสมาชิกจริงในแต่ละระดับ **ตามเกณฑ์ที่ใช้อยู่ตอนนี้** คีย์เป็น id ของระดับ
   *
   * ⚠️ ส่งค่านี้มาแล้วหน้าจอจะ**ปิดการคาดการณ์** ว่าถ้าย้ายเกณฑ์แล้วใครจะย้ายระดับ
   *    เพราะการคาดการณ์ต้องรู้ยอดสะสมของสมาชิกทุกคน ซึ่ง CRM ไม่มี endpoint ให้
   *    (loyalty-tier-summary คืนแค่จำนวนต่อระดับตามเกณฑ์ปัจจุบัน)
   *    แสดงตัวเลขคาดการณ์ที่คำนวณจากข้อมูลจำลองปนกับจำนวนจริงคือการหลอกคนตั้งค่า
   */
  memberCounts?: Map<string, number>;
  /**
   * รหัสสิทธิประโยชน์ที่เลือกได้ — มาจากตารางแม่ของ backend
   *
   * ⚠️ backend เก็บรหัสเป็น enum ตายตัวและมีฟังก์ชันซิงก์ตารางแม่กับ enum
   *    การเพิ่มรหัสใหม่จึงเป็นการแก้โค้ด backend ไม่ใช่การตั้งค่า
   *    หน้าจอนี้จึงให้ **เลือกจากรายการ** ไม่ให้พิมพ์ข้อความเอง
   *
   * ไม่ส่งมา = ใช้ BENEFIT_CATALOG ในไฟล์ข้อมูลจำลอง (ดูหน้าจอโดยไม่ต้องมี backend)
   */
  benefitCatalog?: BenefitCatalogItem[];
  /** ประวัติการแก้ระดับสมาชิกและสิทธิ์ — ไม่ส่งมา = ไม่แสดงส่วนประวัติ */
  auditEntries?: AuditEntry[];
}

export const TierScreen: React.FC<TierScreenProps> = ({
  linkComponent,
  initialState = 'ready', tiers: tiersProp, onSave, memberCounts,
  benefitCatalog, auditEntries,
  capLabel = 'เพดานต่อคนต่อปี', showClaimedCount = true, showImage = true,
}) => {
  const catalog = benefitCatalog ?? BENEFIT_CATALOG;
  const catalogById = new Map(catalog.map((c) => [c.codeId, c]));
  const Link = resolveLink(linkComponent);
  /** คาดการณ์ผลของการย้ายเกณฑ์ได้เฉพาะกับข้อมูลจำลองที่มียอดสะสมรายคน */
  const canForecast = memberCounts === undefined;
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [saved, setSaved] = useState<Tier[]>(tiersProp ?? TIERS);
  const [draft, setDraft] = useState<Tier[]>(tiersProp ?? TIERS);

  /* ข้อมูลจาก API มาทีหลัง (โหลด async) — ต้องรับค่าใหม่ทั้ง state และสถานะหน้าจอ
     ถ้าลืมอัปเดต screenState หน้าจะค้างที่ "กำลังโหลด" ตลอดไป */
  useEffect(() => { if (tiersProp) { setSaved(tiersProp); setDraft(tiersProp); } }, [tiersProp]);
  useEffect(() => { setScreenState(initialState); }, [initialState]);
  /* ว่างไว้ก่อน แล้วให้ effect ด้านล่างเลือกระดับแรกที่มีจริง — id มาจาก backend จึงเดาไม่ได้ */
  const [selected, setSelected] = useState<TierId>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** ระดับที่กำลังจะลบ — ยืนยันก่อน เพราะผลกระทบกับสมาชิกเกิดทันทีที่บันทึก */
  const [confirmRemove, setConfirmRemove] = useState<Tier | null>(null);

  /* division อื่นอาจไม่มีระดับที่เลือกค้างไว้ (เช่นมีแค่ระดับเดียว) — ย้ายไปตัวแรกที่มีจริง */
  useEffect(() => {
    if (draft.length > 0 && !draft.some((t) => t.id === selected)) setSelected(draft[0]!.id);
  }, [draft, selected]);

  /* ถอยไปตัวแรกระหว่างรอ effect ด้านบน — ไม่งั้น render รอบนั้นอ่าน undefined แล้วจอขาว
     ถ้า draft ว่างจริง จะ return หน้าว่างก่อนถึงจุดที่ใช้ค่านี้ */
  const tier = (draft.find((t) => t.id === selected) ?? draft[0])!;
  /* ระดับที่เพิ่งเพิ่มยังไม่มีค่าเดิม — ใช้ undefined ไม่ใช่ระดับอื่นแทน */
  const savedTier = saved.find((t) => t.id === tier?.id);
  const tierIndex = draft.findIndex((t) => t.id === tier?.id);
  const styleOf = (id: TierId) => styleAt(draft.findIndex((t) => t.id === id));

  const addTier = () => {
    const top = draft[draft.length - 1];
    const next: Tier = {
      id: `${NEW_TIER_PREFIX}${Date.now()}`,
      name: '',
      /* ต้องสูงกว่าระดับบนสุด ไม่งั้นลำดับผิดทันทีที่เพิ่ม */
      minSpend: top ? top.minSpend + 10_000 : 0,
      maxSpend: null,
      benefits: [],
      imageUrl: null,
      imageAlt: '',
      memberCount: 0,
      provisional: false,
    };
    setDraft((ts) => [...ts, next]);
    setSelected(next.id);
  };

  const removeTier = (id: TierId) => {
    setDraft((ts) => ts.filter((t) => t.id !== id));
    setConfirmRemove(null);
  };

  const patch = (id: TierId, next: Partial<Tier>) =>
    setDraft((ts) => ts.map((t) => (t.id === id ? { ...t, ...next } : t)));

  const patchBenefit = (tierId: TierId, benefitId: string, next: Partial<TierBenefit>) =>
    setDraft((ts) => ts.map((t) => (t.id === tierId
      ? { ...t, benefits: t.benefits.map((b) => (b.id === benefitId ? { ...b, ...next, provisional: false } : b)) }
      : t)));

  /* ขั้นสูงของแต่ละระดับคำนวณจากขั้นต่ำของระดับถัดไป — ช่องว่างจึงเกิดขึ้นไม่ได้ */
  const ceilingOf = useCallback(
    (i: number): number | null => (i + 1 < draft.length ? draft[i + 1]!.minSpend : null),
    [draft],
  );

  /* --------------------------------------------------------- การกระจายสมาชิก */

  const distribution = useMemo(() => draft.map((t, i) => {
    const max = ceilingOf(i);
    if (!canForecast) {
      /* มีแต่จำนวนตามเกณฑ์ปัจจุบัน — ไม่แต่งตัวเลขคาดการณ์ขึ้นมา */
      const now = memberCounts?.get(t.id) ?? 0;
      return { id: t.id, name: t.name, min: t.minSpend, max, now, next: now, delta: 0 };
    }
    /* จับคู่กับค่าเดิมด้วย id — ลำดับเปลี่ยนได้เมื่อเพิ่มหรือลบระดับ */
    const si = saved.findIndex((x) => x.id === t.id);
    const now = si < 0 ? 0 : membersInRange(saved[si]!.minSpend, si + 1 < saved.length ? saved[si + 1]!.minSpend : null);
    const next = membersInRange(t.minSpend, max);
    return { id: t.id, name: t.name, min: t.minSpend, max, now, next, delta: next - now };
  }), [draft, saved, ceilingOf, canForecast, memberCounts]);

  const thresholdError = useMemo(() => {
    /* backend ไม่ตรวจอะไรเลย — ชื่อซ้ำหรือเกณฑ์ซ้ำทำให้ระบบเลือกระดับให้สมาชิกแบบเดาไม่ได้ */
    if (draft.some((t) => !t.name.trim())) return 'ต้องตั้งชื่อระดับให้ครบทุกระดับ';
    const names = draft.map((t) => t.name.trim().toLowerCase());
    if (new Set(names).size !== names.length) return 'ชื่อระดับซ้ำกัน';
    for (let i = 1; i < draft.length; i += 1) {
      if (draft[i]!.minSpend <= draft[i - 1]!.minSpend) {
        return `เกณฑ์ของ ${draft[i]!.name} ต้องมากกว่า ${draft[i - 1]!.name}`;
      }
    }
    if (draft.length > 0 && draft[0]!.minSpend !== 0) return 'ระดับต่ำสุดต้องเริ่มที่ 0 บาท ไม่งั้นสมาชิกใหม่จะไม่อยู่ระดับใดเลย';
    return null;
  }, [draft]);

  /* ------------------------------------------------------- สิ่งที่เปลี่ยนไป */

  const changes = useMemo(() => {
    const out: Array<{ field: string; from: string; to: string }> = [];
    const savedById = new Map(saved.map((x) => [x.id, x] as const));
    draft.forEach((t) => {
      const s = savedById.get(t.id);
      if (!s) {
        out.push({ field: 'เพิ่มระดับ', from: 'ไม่มี', to: `${t.name || '(ยังไม่ตั้งชื่อ)'} — เริ่มที่ ${t.minSpend.toLocaleString('th-TH')} บาท` });
        return;
      }
      if (t.name !== s.name) out.push({ field: 'ชื่อระดับ', from: s.name, to: t.name });
      if (t.minSpend !== s.minSpend) {
        out.push({ field: `เกณฑ์ยอดซื้อ ${t.name}`, from: `${s.minSpend.toLocaleString('th-TH')} บาท`, to: `${t.minSpend.toLocaleString('th-TH')} บาท` });
      }
      if (t.imageAlt !== s.imageAlt || t.imageUrl !== s.imageUrl) {
        out.push({ field: `ภาพประกอบ ${t.name}`, from: s.imageUrl ? 'มีภาพ' : 'ไม่มีภาพ', to: t.imageUrl ? 'ภาพใหม่' : 'เอาออก' });
      }
      const sIds = s.benefits.map((b) => b.id);
      t.benefits.forEach((b, bi) => {
        const before = s.benefits.find((x) => x.id === b.id);
        if (!before) {
          out.push({ field: `${t.name} — สิทธิ์ใหม่ลำดับ ${bi + 1}`, from: 'ไม่มี', to: b.text || '(ยังไม่กรอกข้อความ)' });
          return;
        }
        if (before.text !== b.text) out.push({ field: `${t.name} — ข้อความสิทธิ์ที่ ${bi + 1}`, from: before.text, to: b.text });
        if (before.claimable !== b.claimable) out.push({ field: `${t.name} — ปุ่มกดรับสิทธิ์ที่ ${bi + 1}`, from: before.claimable ? 'มี' : 'ไม่มี', to: b.claimable ? 'มี' : 'ไม่มี' });
        if (before.capPerYear !== b.capPerYear) {
          out.push({
            field: `${t.name} — เพดานต่อคนต่อปีของสิทธิ์ที่ ${bi + 1}`,
            from: before.capPerYear === null ? 'ไม่จำกัด' : `${before.capPerYear} ครั้ง`,
            to: b.capPerYear === null ? 'ไม่จำกัด' : `${b.capPerYear} ครั้ง`,
          });
        }
      });
      sIds.filter((id) => !t.benefits.some((b) => b.id === id)).forEach((id) => {
        out.push({ field: `${t.name} — ลบสิทธิ์`, from: s.benefits.find((b) => b.id === id)!.text, to: 'ลบออก' });
      });
    });
    saved.filter((x) => !draft.some((t) => t.id === x.id)).forEach((x) => {
      out.push({ field: 'ลบระดับ', from: x.name, to: 'ลบออก' });
    });
    return out;
  }, [draft, saved]);

  const removedTiers = saved.filter((x) => !draft.some((t) => t.id === x.id));

  /* ข้อความว่างเป็นไปไม่ได้แล้ว เพราะสิทธิ์มาจากการเลือกรหัส ไม่ใช่การพิมพ์
     เหลือไว้เป็นยามกันข้อมูลจากฝั่ง API ที่ไม่มีรหัสตรงกับตารางแม่ */
  const emptyBenefit = draft.some((t) => t.benefits.some((b) => !b.codeId));
  const missingAlt = draft.some((t) => t.imageUrl && !t.imageAlt.trim());
  const blockedReason = thresholdError
    ?? (emptyBenefit ? 'มีสิทธิ์ที่ไม่มีรหัสกำกับ' : undefined)
    ?? (missingAlt ? 'มีภาพที่ยังไม่ได้กรอกข้อความแทนภาพ' : undefined);

  const doSave = (reason?: string) => {
    const before = saved;
    setSaved(draft);
    setConfirmOpen(false);

    /* ส่งให้ backend ทีหลัง — ถ้าล้มเหลวคืนค่าเดิมและบอกให้ชัดว่ายังไม่ถูกบันทึก */
    void onSave?.(draft, reason).catch((err: unknown) => {
      setSaved(before);
      setDraft(before);
      toast.show({
        tone: 'error',
        message: `บันทึกไม่สำเร็จ — ${err instanceof Error ? err.message : 'กรุณาลองใหม่'} ค่าถูกคืนกลับเป็นของเดิมแล้ว`,
      });
    });

    toast.show({
      tone: 'success',
      message: 'บันทึกระดับสมาชิกและสิทธิประโยชน์แล้ว — ข้อความใหม่ขึ้นบนแอปทันที · บันทึกใน Audit Log',
      undo: { onUndo: () => { setSaved(before); setDraft(before); void onSave?.(before, undoReason(reason)); }, seconds: 10 },
    });
  };

  const stats: SummaryStat[] = [
    ...draft.map((t, i): SummaryStat => ({
      label: t.name,
      value: distribution[i]!.next.toLocaleString('th-TH') + ' คน',
      icon: styleAt(i).icon,
      tone: i === draft.length - 1 ? 'brand' : 'default',
      note: distribution[i]!.delta !== 0 ? `${distribution[i]!.delta > 0 ? '+' : ''}${distribution[i]!.delta.toLocaleString('th-TH')}` : undefined,
    })),
    {
      label: 'สิทธิ์ที่กดรับได้ทั้งหมด',
      value: draft.reduce((s, t) => s + t.benefits.filter((b) => b.claimable).length, 0).toLocaleString('th-TH') + ' รายการ',
      icon: 'redeem',
    },
  ];

  /* ------------------------------------------------------------------ พรีวิว */

  /**
   * ไม่มีระดับให้แสดง — ปกติ container ดักไว้ก่อนแล้ว แต่กันไว้อีกชั้น
   * ⚠️ ต้องอยู่หลัง hook ทุกตัว และก่อนโค้ดที่อ่าน `tier`
   */
  if (draft.length === 0) {
    return (
      <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
        <EmptyState kind="no-data" title="ยังไม่มีระดับสมาชิก" description="division นี้ยังไม่ได้ตั้งระดับสมาชิก" />
      </div>
    );
  }

  const preview = (
    <PhoneFrame
      screenLabel="สิทธิประโยชน์ของฉัน"
      caption={<>พรีวิวจากข้อความที่กรอกไว้จริง — ข้อความในกรอบนี้ใช้กฎของแอปมือถือ (16px · line-height 1.6 · พื้นที่กด 44px)</>}
    >
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          style={{
            background: styleAt(tierIndex).bg, color: styleAt(tierIndex).text,
            border: `1px solid ${styleAt(tierIndex).border}`,
            borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 4,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1.6, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name={styleAt(tierIndex).icon} size={20} filled />
            {tier.name} Member
          </span>
          <span style={{ fontSize: 16, lineHeight: 1.6 }}>
            ยอดสะสมปีนี้ {tier.minSpend.toLocaleString('th-TH')}
            {ceilingOf(tierIndex) === null ? ' บาทขึ้นไป' : ` – ${ceilingOf(tierIndex)!.toLocaleString('th-TH')} บาท`}
          </span>
        </div>

        {tier.imageUrl && (
          <img
            src={tier.imageUrl}
            alt={tier.imageAlt || 'ภาพประกอบสิทธิประโยชน์'}
            style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 12, display: 'block' }}
          />
        )}

        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tier.benefits.map((b) => (
            <li
              key={b.id}
              style={{
                border: '1px solid var(--line-200)', borderRadius: 12, padding: 12,
                display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 16, lineHeight: 1.6, color: 'var(--ink-900)' }}>
                <Icon name="check_circle" size={20} filled style={{ color: 'var(--success-text)', flexShrink: 0, marginTop: 3 }} />
                <span>{b.text || 'ยังไม่กรอกข้อความ'}</span>
              </span>
              {b.claimable && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    tabIndex={-1}
                    style={{
                      minHeight: 44, padding: '0 20px', borderRadius: 999, border: 0,
                      background: 'var(--blue-700)', color: 'var(--text-on-blue)', fontSize: 16, fontWeight: 600,
                      fontFamily: 'var(--font-thai)', cursor: 'default',
                    }}
                  >
                    กดรับสิทธิ์
                  </button>
                  {b.capPerYear !== null && (
                    <span style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-700)' }}>
                      {b.capPerYear} สิทธิ์/ปี
                    </span>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </PhoneFrame>
  );

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="ระดับสมาชิกและสิทธิประโยชน์"
        end={
          <Link to="/reports/tiers" className={secondaryBtnCls} style={{ textDecoration: 'none' }}>
            <Icon name="analytics" size={17} />
            รายงานการปรับระดับ
          </Link>
        }
      />

      <SummaryStrip stats={stats} />

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 gap-[var(--space-4)] overflow-y-auto p-[var(--space-4)]">
          <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-4)]">
            {/* 1. เกณฑ์ยอดซื้อ */}
            <FormSection
              title="เกณฑ์ยอดซื้อสะสมต่อปี"
              requirement="FR-ADM-23 · BR-04"
              description="ขั้นสูงของแต่ละระดับคำนวณจากขั้นต่ำของระดับถัดไป จึงไม่มีช่องว่างหรือช่วงทับกันได้"
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-[length:var(--text-table)]">
                  <caption className="sr-only">เกณฑ์ยอดซื้อและจำนวนสมาชิกของแต่ละระดับ</caption>
                  <thead>
                    <tr className="border-b border-[var(--line-300)] text-left text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
                      <th scope="col" className="py-[var(--space-2)] font-[var(--weight-medium)]">ระดับ</th>
                      <th scope="col" className="py-[var(--space-2)] font-[var(--weight-medium)]">ยอดสะสมขั้นต่ำ (บาท)</th>
                      <th scope="col" className="py-[var(--space-2)] font-[var(--weight-medium)]">ช่วงที่ครอบคลุม</th>
                      <th scope="col" className="py-[var(--space-2)] text-right font-[var(--weight-medium)]">
                        {canForecast ? 'สมาชิกตามเกณฑ์ใหม่' : 'สมาชิกตามเกณฑ์ปัจจุบัน'}
                      </th>
                      {canForecast && (
                        <th scope="col" className="py-[var(--space-2)] text-right font-[var(--weight-medium)]">เปลี่ยนจากเดิม</th>
                      )}
                      <th scope="col" className="w-[52px] py-[var(--space-2)]"><span className="sr-only">ลบระดับ</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.map((t, i) => {
                      const d = distribution[i]!;
                      return (
                        <tr key={t.id} className="border-b border-[var(--line-200)]">
                          <td className="py-[var(--space-2)]">
                            <span className="flex items-center gap-[var(--space-15)]">
                              <Icon name={styleAt(i).icon} size={17} filled style={{ color: styleAt(i).onLight }} />
                              <input
                                type="text"
                                value={t.name}
                                onChange={(e) => patch(t.id, { name: e.target.value })}
                                placeholder="ชื่อระดับ"
                                aria-label={`ชื่อระดับที่ ${i + 1}`}
                                className={`${inputCls} w-[160px] font-[var(--weight-medium)]`}
                              />
                            </span>
                          </td>
                          <td className="py-[var(--space-2)]">
                            <NumberField
                              value={t.minSpend}
                              onChange={(v) => patch(t.id, { minSpend: v === '' ? 0 : v })}
                              min={0}
                              step={500}
                              width={148}
                              disabled={i === 0}
                              ariaLabel={`ยอดสะสมขั้นต่ำของระดับ ${t.name}`}
                            />
                          </td>
                          <td className="bo-num py-[var(--space-2)] text-[var(--ink-700)]" style={{ textAlign: 'left' }}>
                            {t.minSpend.toLocaleString('th-TH')}
                            {d.max === null ? ' บาทขึ้นไป' : ` – ${d.max.toLocaleString('th-TH')} บาท`}
                          </td>
                          <td className="bo-num py-[var(--space-2)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                            {d.next.toLocaleString('th-TH')}
                          </td>
                          {canForecast && (
                            <td className="py-[var(--space-2)] text-right">
                              {d.delta === 0 ? (
                                <span className="text-[var(--ink-400)]">—</span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] font-[var(--weight-medium)]"
                                  style={{ color: d.delta > 0 ? 'var(--success-text)' : 'var(--warning-text)' }}
                                >
                                  <Icon name={d.delta > 0 ? 'trending_up' : 'trending_down'} size={15} filled />
                                  <span className="bo-num">{d.delta > 0 ? '+' : ''}{d.delta.toLocaleString('th-TH')} คน</span>
                                </span>
                              )}
                            </td>
                          )}
                          <td className="py-[var(--space-2)] text-right">
                            {/* ลบระดับสุดท้ายไม่ได้ — สมาชิกทุกคนต้องอยู่ในระดับใดระดับหนึ่งเสมอ */}
                            <button
                              type="button"
                              disabled={draft.length <= 1}
                              onClick={() => setConfirmRemove(t)}
                              aria-label={`ลบระดับ ${t.name || `ที่ ${i + 1}`}`}
                              title={draft.length <= 1 ? 'ต้องมีอย่างน้อยหนึ่งระดับ' : `ลบระดับ ${t.name}`}
                              className="inline-flex min-h-[var(--hit-icon-sm)] min-w-[var(--hit-icon-sm)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--error-text)] hover:bg-[var(--error-bg)] disabled:cursor-not-allowed disabled:text-[var(--text-disabled)] disabled:hover:bg-transparent outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                            >
                              <Icon name="delete" size={17} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div>
                <button type="button" onClick={addTier} className={secondaryBtnCls}>
                  <Icon name="add" size={17} />
                  เพิ่มระดับ
                </button>
              </div>

              {thresholdError ? (
                <Callout tone="error">{thresholdError}</Callout>
              ) : !canForecast ? null : distribution.some((d) => d.delta !== 0) && (
                <Callout tone="warning" title="การขยับเกณฑ์ทำให้สมาชิกย้ายระดับ">
                  สมาชิกที่ถูกลดระดับจะเสียสิทธิ์ที่ยังไม่ได้ใช้ในรอบปีนี้ —
                  ก่อนบันทึกควรตกลงกับฝ่ายการตลาดว่าจะคงระดับเดิมไว้จนจบรอบปี หรือให้เปลี่ยนทันที
                  (นโยบายนี้ตั้งได้ในหน้ากติกาคะแนน หัวข้อ &quot;รอบรีเซ็ตยอดสะสมรายปี&quot;)
                </Callout>
              )}
            </FormSection>

            {/* 2. สิทธิประโยชน์ */}
            <FormSection
              title="สิทธิประโยชน์ของแต่ละระดับ"
              requirement="FR-ADM-23"
              description="เลือกจากรหัสสิทธิ์ที่ระบบมี — ข้อความที่ลูกค้าเห็นมาจากรหัสที่เลือก แก้ที่นี่ไม่ได้"
              action={
                <label className="flex items-center gap-[var(--space-2)]">
                  <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">
                    เพิ่มสิทธิ์ใน {tier.name}
                  </span>
                  <select
                    value=""
                    onChange={(e) => {
                      const item = catalogById.get(e.target.value);
                      if (!item) return;
                      patch(selected, {
                        benefits: [...tier.benefits, {
                          /* id ชั่วคราว — ชั้น API รู้ว่า 'new-' คือรายการที่ต้อง link ใหม่ */
                          id: `new-${item.codeId}`,
                          codeId: item.codeId,
                          text: item.text,
                          claimable: false,
                          capPerYear: null,
                          claimedThisYear: 0,
                          provisional: false,
                        }],
                      });
                    }}
                    aria-label={`เลือกรหัสสิทธิ์เพื่อเพิ่มใน ${tier.name}`}
                    className={inputCls}
                    style={{ width: 260 }}
                  >
                    <option value="">— เลือกรหัสสิทธิ์ —</option>
                    {catalog
                      .filter((c) => !tier.benefits.some((b) => b.codeId === c.codeId))
                      .map((c) => (
                        <option key={c.codeId} value={c.codeId}>
                          {c.code} · {c.text}
                        </option>
                      ))}
                  </select>
                </label>
              }
            >
              <div role="tablist" aria-label="เลือกระดับสมาชิก" className="flex items-end gap-[var(--space-1)] border-b border-[var(--line-200)]">
                {draft.map((t) => {
                  const on = t.id === selected;
                  return (
                    <button
                      key={t.id}
                      role="tab"
                      type="button"
                      aria-selected={on}
                      onClick={() => setSelected(t.id)}
                      className={`inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] border-b-2 px-[var(--space-3)] text-[length:var(--text-form-lg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1 ${
                        on ? 'border-[var(--blue-700)] font-[var(--weight-semibold)] text-[var(--blue-700)]' : 'border-transparent text-[var(--ink-700)] hover:border-[var(--line-300)] hover:text-[var(--ink-900)]'
                      }`}
                    >
                      <Icon name={styleOf(t.id).icon} size={18} filled={on} />
                      {t.name}
                      <span className={`bo-num rounded-[var(--radius-pill)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] ${on ? 'bg-[var(--blue-700)] text-white' : 'bg-[var(--surface-100)] text-[var(--ink-700)]'}`}>
                        {t.benefits.length}
                      </span>
                    </button>
                  );
                })}
              </div>

              <ul className="m-0 flex list-none flex-col gap-[var(--space-3)] p-0">
                {tier.benefits.map((b, i) => {
                  const savedBenefit = savedTier?.benefits.find((x) => x.id === b.id);
                  const item = catalogById.get(b.codeId);
                  const isNew = b.id.startsWith('new-');
                  return (
                    <li key={b.id} className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
                      <div className="flex items-start gap-[var(--space-2)]">
                        <span className="bo-num mt-[var(--space-2)] w-[24px] shrink-0 text-[length:var(--text-table-sm)] text-[var(--ink-600)]">{i + 1}.</span>
                        <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-1)]">
                          {/* ข้อความมาจากรหัสที่เลือก แก้ที่นี่ไม่ได้ — ตารางแม่ของรหัสอยู่ฝั่ง backend */}
                          <span className="flex flex-wrap items-center gap-[var(--space-2)]">
                            <span className="bo-num inline-flex items-center rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-100)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]" style={{ textAlign: 'left' }}>
                              {item?.code ?? b.codeId}
                            </span>
                            <span className="text-[length:var(--text-form-lg)] text-[var(--ink-900)]">
                              {item?.text ?? b.text}
                            </span>
                          </span>
                          <span className="flex flex-wrap items-center gap-[var(--space-3)] text-[length:var(--text-table-sm)]">
                            {!item && (
                              <span className="flex items-center gap-1 text-[var(--warning-text)]">
                                <Icon name="help" size={14} filled />
                                ไม่พบสิทธิ์นี้ในรายการของระบบแล้ว — อาจถูกปิดใช้งานไปแล้ว
                              </span>
                            )}
                            {isNew && (
                              <span className="flex items-center gap-1 text-[var(--blue-700)]">
                                <Icon name="add_circle" size={14} filled />
                                เพิ่มใหม่ ยังไม่บันทึก
                              </span>
                            )}
                          </span>
                        </div>
                        <span className="flex shrink-0 items-center gap-[var(--space-05)]">
                          {/* ไม่มีปุ่มเลื่อนลำดับ — ลำดับแสดงผลมาจาก display_seq ของรหัสในตารางแม่
                              ไม่ใช่ค่าที่ตั้งต่อระดับได้ การมีปุ่มจะทำให้เข้าใจผิดว่าเรียงเองได้ */}
                          <button
                            type="button"
                            aria-label={`ลบสิทธิ์ที่ ${i + 1}`}
                            onClick={() => patch(tier.id, { benefits: tier.benefits.filter((x) => x.id !== b.id) })}
                            className="inline-flex min-h-[var(--hit-icon-sm)] min-w-[var(--hit-icon-sm)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--error-text)] hover:bg-[var(--error-bg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                          >
                            <Icon name="delete" size={17} />
                          </button>
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-[var(--space-4)] border-t border-[var(--line-200)] pt-[var(--space-2)] pl-[var(--space-8)]">
                        <label className="flex cursor-pointer items-center gap-[var(--space-15)] text-[length:var(--text-form)] text-[var(--ink-900)]">
                          <input
                            type="checkbox"
                            checked={b.claimable}
                            /* ⚠️ ห้ามล้าง capPerYear ตอนเอาติ๊กออก — ค่าเพดานมีที่เก็บของตัวเอง
                               เคยล้างแล้วบันทึกทับเป็น "ไม่จำกัด" โดยที่คนแก้ไม่รู้ตัว */
                            onChange={(e) => patchBenefit(tier.id, b.id, { claimable: e.target.checked })}
                            className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                          />
                          มีปุ่มให้ลูกค้ากดรับสิทธิ์
                        </label>

                        {b.claimable && (
                          <>
                            <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
                              {capLabel}
                              <NumberField
                                value={b.capPerYear ?? ''}
                                onChange={(v) => patchBenefit(tier.id, b.id, { capPerYear: v === '' ? null : v })}
                                unit="ครั้ง"
                                min={1}
                                width={132}
                                ariaLabel={`เพดานการใช้สิทธิ์ต่อคนต่อปีของสิทธิ์ที่ ${i + 1}`}
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => patchBenefit(tier.id, b.id, { capPerYear: null })}
                              className={ghostBtnCls}
                            >
                              ไม่จำกัด
                            </button>
                            {showClaimedCount && (
                              <span className="flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                                <Icon name="how_to_reg" size={15} />
                                ปีนี้กดรับไปแล้ว <strong className="bo-num">{b.claimedThisYear.toLocaleString('th-TH')}</strong> ครั้ง
                              </span>
                            )}
                            {b.capPerYear !== null && savedBenefit && savedBenefit.capPerYear !== null && b.capPerYear < savedBenefit.capPerYear && (
                              <span className="flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--warning-text)]">
                                <Icon name="warning" size={15} filled />
                                ลดเพดานกลางรอบปี — คนที่ใช้เกินค่าใหม่ไปแล้วจะกดรับต่อไม่ได้
                              </span>
                            )}
                          </>
                        )}

                        {b.provisional && (
                          <span className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--warning-bg)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--warning-text)]">
                            <Icon name="pending_actions" size={13} />
                            ค่าตั้งต้นจากเอกสาร
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {tier.benefits.length === 0 && (
                <Callout tone="warning">
                  ระดับ {tier.name} ยังไม่มีสิทธิประโยชน์เลย — การ์ดระดับในแอปจะว่างเปล่า
                </Callout>
              )}
            </FormSection>

            {/* 3. ภาพประกอบ */}
            {showImage && (
            <FormSection
              title={`ภาพประกอบสิทธิประโยชน์ — ${tier.name}`}
              description="ภาพนี้ขึ้นบนหน้าสิทธิประโยชน์ในแอป เหนือรายการสิทธิ์"
            >
              <ImageDropzone
                value={tier.imageUrl ? { url: tier.imageUrl, alt: tier.imageAlt } : null}
                onChange={(v: ImageValue | null) => patch(tier.id, { imageUrl: v?.url ?? null, imageAlt: v?.alt ?? '' })}
                spec={{
                  aspect: '16:9',
                  minWidth: 1_280,
                  maxKB: 400,
                  usedAt: 'หน้าสิทธิประโยชน์ในแอป กว้างเต็มจอ 390px — ภาพถูกย่อลง ไม่ครอป',
                }}
                label={`ภาพ 16:9 ของระดับ ${tier.name}`}
                previewWidth={352}
              />
            </FormSection>
            )}

            <FormRow
              label="หมายเหตุถึงทีมการตลาด"
              help="ไม่แสดงในแอป — ใช้บันทึกว่าข้อความชุดนี้ผ่านการตรวจจากใครแล้ว"
            >
              <textarea className={textareaCls} placeholder="เช่น ข้อความชุดนี้ผ่านการตรวจจากคุณฟ้าแล้ว 20 ส.ค. เหลือรอยืนยันเกณฑ์ Platinum" />
            </FormRow>

            {/* 5. ประวัติการแก้ไข — จาก AuditLog ของ backend
                   ครอบคลุมทั้งการแก้ระดับและการผูก/ถอดสิทธิ์ */}
            {auditEntries && (
              <FormSection
                title="ประวัติการแก้ไข"
                description="บันทึกอัตโนมัติทุกครั้งที่มีการแก้ระดับสมาชิกหรือสิทธิประโยชน์"
              >
                <AuditTrail entries={auditEntries} />
              </FormSection>
            )}

            <div className="pb-[var(--space-4)]" />
          </div>

          <aside className="sticky top-0 flex shrink-0 flex-col gap-[var(--space-3)] self-start">
            {preview}
            <div className="flex max-w-[420px] flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
              <h3 className="m-0 text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">สิทธิ์ที่กดรับได้ในระดับนี้</h3>
              <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                {tier.benefits.filter((b) => b.claimable).length === 0
                  ? 'ยังไม่มีสิทธิ์ที่กดรับได้ — ลูกค้าจะเห็นเฉพาะข้อความ ไม่มีปุ่ม'
                  : `${tier.benefits.filter((b) => b.claimable).length} รายการ · การตั้งอายุของรหัสหลังกดรับอยู่ในหน้า "สิทธิพิเศษแบบกดรับ"`}
              </p>
            </div>
          </aside>
        </div>

        <SaveBar
          changeCount={changes.length}
          changes={changes}
          onSave={() => setConfirmOpen(true)}
          onDiscard={() => setDraft(saved)}
          blockedReason={blockedReason}
          impact={
            <span>
              ข้อความและสิทธิ์ใหม่ขึ้นบนแอปทันทีที่บันทึก · กระทบสมาชิก{' '}
              <strong className="bo-num">{distribution.reduce((s, d) => s + d.next, 0).toLocaleString('th-TH')}</strong> คน
            </span>
          }
        />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        tone={removedTiers.length > 0 || distribution.some((d) => d.delta !== 0) ? 'danger' : 'default'}
        title="บันทึกระดับสมาชิกและสิทธิประโยชน์"
        description={
          <>
            <p className="m-0 mb-[var(--space-2)]">
              แก้ไป {changes.length.toLocaleString('th-TH')} รายการ — ข้อความสิทธิ์ที่บันทึกจะขึ้นบนแอปทันที
              โดยไม่ต้องรอรอบปล่อยเวอร์ชัน
            </p>
            {removedTiers.length > 0 && (
              <p className="m-0 mb-[var(--space-2)] font-[var(--weight-medium)] text-[var(--error-text)]">
                ลบระดับ {removedTiers.map((t) => t.name).join(' · ')} — สมาชิกในระดับนี้จะย้ายไปตามเกณฑ์ที่เหลือทันทีที่บันทึก
              </p>
            )}
            {distribution.filter((d) => d.delta !== 0).map((d) => (
              <p key={d.id} className="m-0 text-[var(--ink-900)]">
                {d.name}: {d.now.toLocaleString('th-TH')} → <strong className="bo-num">{d.next.toLocaleString('th-TH')}</strong> คน
              </p>
            ))}
          </>
        }
        confirmLabel="บันทึกและขึ้นแอป"
        requireReason
        reasonLabel="เหตุผลที่แก้"
        reasonPresets={[
          'แก้ข้อความตามที่ฝ่ายการตลาดตรวจแล้ว',
          'ปรับเกณฑ์ตามที่ที่ประชุมอนุมัติ',
          'เพิ่มสิทธิ์ใหม่ตามแคมเปญที่กำลังจะเริ่ม',
        ]}
        onConfirm={doSave}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* ลบระดับ — ยังไม่ลบจริงจนกว่าจะกดบันทึก แต่ต้องบอกผลให้ครบก่อน เพราะย้อนกลับไม่ได้หลังบันทึก */}
      <ConfirmDialog
        open={Boolean(confirmRemove)}
        tone="danger"
        title={`ลบระดับ ${confirmRemove?.name || ''}`}
        description={confirmRemove ? (
          <>
            <p className="m-0 mb-[var(--space-2)]">เมื่อกดบันทึก จะเกิดสิ่งเหล่านี้ทันที</p>
            <ul className="m-0 list-disc pl-[var(--space-5)]">
              <li>สมาชิกในระดับนี้
                {memberCounts?.get(confirmRemove.id) !== undefined && (
                  <> {(memberCounts.get(confirmRemove.id) ?? 0).toLocaleString('th-TH')} คน</>
                )}
                {' '}ย้ายไปอยู่ระดับตามเกณฑ์ที่เหลือ
              </li>
              <li>สิทธิประโยชน์ {confirmRemove.benefits.length.toLocaleString('th-TH')} รายการที่ผูกกับระดับนี้ถูกลบ</li>
              <li>กู้คืนไม่ได้ — ต้องสร้างระดับและผูกสิทธิ์ใหม่เอง</li>
            </ul>
          </>
        ) : ''}
        confirmLabel="ลบระดับนี้"
        onConfirm={() => { if (confirmRemove) removeTier(confirmRemove.id); }}
        onCancel={() => setConfirmRemove(null)}
      />

      {screenState === 'error' && (
        <div className="p-[var(--space-4)]">
          <Callout tone="error" title="โหลดข้อมูลระดับสมาชิกไม่สำเร็จ">เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ — ลองใหม่อีกครั้ง</Callout>
        </div>
      )}
    </div>
  );
};

export default TierScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-23   แก้ข้อความสิทธิประโยชน์ของแต่ละระดับได้เอง และตั้งเกณฑ์ยอดซื้อของแต่ละระดับ
   BR-03       ยอดสะสมคำนวณในรอบปีและรีเซ็ตทุกปี (นโยบายวันรีเซ็ตอยู่ในหน้ากติกาคะแนน)
   BR-04       เกณฑ์ตั้งต้น Silver 0–5,000 · Gold 5,000–20,000 · Platinum เกิน 20,000
   BR-05       เพดานการใช้สิทธิ์ต่อคนต่อปี — ผูกกับสิทธิ์แต่ละรายการ ไม่ใช่กับระดับ
   SRS 3.4     สิทธิ์แต่ละรายการมีปุ่มกดรับ และบันทึกว่าใช้ไปแล้วกี่ครั้ง
   FR-HOME-01  ชื่อระดับและยอดสะสมที่แสดงในแอปมาจากค่าที่ตั้งในหน้านี้
   FR-ADM-37   ทุกการแก้ไขเข้า Audit Log พร้อมค่าเดิม→ค่าใหม่

   ประเด็นค้าง (⛔):
   — สมาชิกที่ถูกลดระดับเพราะเกณฑ์เปลี่ยน ต้องคงสิทธิ์เดิมจนจบรอบปีหรือเปลี่ยนทันที
     (เกี่ยวกับ คำถามที่ยังค้างกับทีม backend — ยังไม่ได้คำตอบ)
   — ข้อความสิทธิ์ที่ยาวเกิน 62 ตัวอักษรตัดสองบรรทัดบนจอ 390px
     ค่านี้วัดจากฟอนต์ Prompt 16px ถ้าเปลี่ยนฟอนต์หรือขนาดต้องวัดใหม่
   — ยังไม่มีหลายภาษา ข้อความสิทธิ์มีชุดเดียว (ไทย) ถ้าต้องรองรับอังกฤษ
     ต้องเพิ่มช่องข้อความคู่ทุกรายการและตัวเลือกภาษาในพรีวิว
   — สิทธิ์ที่เขียนว่า "สิทธิ์ทั้งหมดของ Silver" เป็นข้อความ ไม่ใช่การอ้างอิงจริง
     ถ้าแก้สิทธิ์ Silver ข้อความใน Gold ไม่เปลี่ยนตาม — ต้องตกลงว่าจะทำเป็นการอ้างอิงหรือไม่
   ============================================================================ */
